// 공개 링크 화면에 무엇을 보일지 — 인메모리·Supabase 가 같이 쓰는 한 벌(대표 분리 · 신뢰 지표 · 후기 · B 이야기).

import { ORG_SETTING_DEFAULT, parseSettingNumber } from "../org/types";
import { EXCERPT_DATE_RE } from "../portfolio/draft-sections";
import { publishedInOrder, showsPublicField, type PublicPortfolioCard } from "../portfolio/public-view";
import type { PortfolioTimelineEntry, PublicPortfolioDetailRow, PublicPortfolioRow } from "../portfolio/types";
import { MAX_FEATURED_PORTFOLIOS } from "./types";
import { isPublicReview } from "../review/rules";
import type { PublicReviewRow } from "../review/types";

// 공개 링크에 싣는 후기 수.
export const PUBLIC_PAGE_REVIEW_LIMIT = 6;

// D7 신뢰 지표 — 자동 집계만 · 값이 없으면 null(화면이 그 줄을 뺀다).
export interface PublicPageTrust {
  finished_count: number | null;
  warranty_months: number | null;
  license_no: string | null;
}

export interface PublicPageReview {
  id: string;
  author_label: string;
  body: string;
}

export const toPublicPageTrust = (
  finishedCount: number,
  warrantySetting: string | null,
  licenseSetting: string | null
): PublicPageTrust => {
  const months = parseSettingNumber(warrantySetting, ORG_SETTING_DEFAULT["contract.warranty_months"]);
  const license = (licenseSetting ?? "").trim();
  return {
    finished_count: finishedCount > 0 ? finishedCount : null,
    warranty_months: Number.isInteger(months) && months > 0 ? months : null,
    license_no: license === "" ? null : license,
  };
};

export const hasPublicPageTrust = (trust: PublicPageTrust): boolean =>
  trust.finished_count !== null || trust.warranty_months !== null || trust.license_no !== null;

// 공개 동의·숨김 아님만 · 표기·글만 남긴다.
export const toPublicPageReviews = (rows: readonly PublicReviewRow[], limit = PUBLIC_PAGE_REVIEW_LIMIT): PublicPageReview[] =>
  rows
    .filter(isPublicReview)
    .slice(0, limit)
    .map((row) => ({ id: row.id, author_label: row.author_label, body: row.body }));

// 대표 사례는 목록에서 빼 맨 위에 따로 둔다.
export const withoutHero = (cards: readonly PublicPortfolioCard[], heroId: string | null): PublicPortfolioCard[] =>
  cards.filter((card) => card.id !== heroId);

// B 이야기의 대표 현장 한 줄 — 「현장일지 · 07-29 · 목공」 과 발췌 글.
export interface PublicPageExcerpt {
  caption: string;
  text: string;
}

// B 이야기 — 대표 현장의 공개 항목(공정 챕터·기간·일지 한 줄)과 아래에 늘어놓을 다른 현장.
export interface PublicPageStory {
  chapter_count: number | null;
  period: string | null;
  excerpt: PublicPageExcerpt | null;
  others: PublicPortfolioCard[];
  // 공개 사례 전체 수 — 다 못 보이면 「모두 보기」 로 목록에 보낸다.
  total: number;
}

// B 의 대표 — 링크가 고른 사례가 공개면 그것, 아니면 목록 맨 앞(사장님이 정한 순서의 첫 사례).
export const storyHeroIdOf = (
  heroId: string | null,
  rows: readonly Pick<PublicPortfolioRow, "id" | "published" | "sort_order">[]
): string | null => {
  const ordered = publishedInOrder(rows);
  return ordered.find((row) => row.id === heroId)?.id ?? ordered[0]?.id ?? null;
};

// 「2026-07 ~ 08」 — 챕터 날짜의 처음·끝 달(날짜가 하나도 없으면 null).
export const storyPeriodOf = (timeline: readonly Pick<PortfolioTimelineEntry, "start" | "end">[]): string | null => {
  const dates = timeline.flatMap((c) => [c.start, c.end]).filter((d): d is string => typeof d === "string" && d.length >= 7);
  if (dates.length === 0) return null;
  const sorted = [...dates].sort();
  const from = sorted[0].slice(0, 7);
  const to = sorted[sorted.length - 1].slice(0, 7);
  if (from === to) return from;
  return from.slice(0, 4) === to.slice(0, 4) ? `${from} ~ ${to.slice(5)}` : `${from} ~ ${to}`;
};

// 첫 발췌 한 줄 — 앞의 「MM-DD 」 는 캡션으로 옮기고, 기간을 숨기면 버린다(사장님이 고친 줄은 날짜 없이 그대로).
export const storyExcerptOf = (
  timeline: readonly Pick<PortfolioTimelineEntry, "name" | "excerpts">[],
  showPeriod = true
): PublicPageExcerpt | null => {
  for (const chapter of timeline) {
    const line = chapter.excerpts.map((l) => l.trim()).find(Boolean);
    if (!line) continue;
    const match = EXCERPT_DATE_RE.exec(line);
    const date = match && showPeriod ? match[1] : null;
    return { caption: ["현장일지", date, chapter.name.trim() || null].filter(Boolean).join(" · "), text: match ? match[2] : line };
  }
  return null;
};

// 다른 현장 — 링크가 고른 사례(공개인 것만 · 고른 순서)가 있으면 그것, 없으면 나머지 공개 사례를 앞에서부터.
export const storyOthersOf = (cards: readonly PublicPortfolioCard[], featuredIds: readonly string[]): PublicPortfolioCard[] => {
  const byId = new Map(cards.map((card) => [card.id, card]));
  const picked = featuredIds.flatMap((id) => byId.get(id) ?? []);
  return (picked.length > 0 ? picked : [...cards]).slice(0, MAX_FEATURED_PORTFOLIOS);
};

// 공개 항목으로 거른다 — 「공정 챕터」 를 켜야 챕터 수·일지 한 줄, 「공사 기간」 을 켜야 기간.
export const toPublicPageStory = (
  detail: Pick<PublicPortfolioDetailRow, "public_fields" | "timeline"> | null,
  others: readonly PublicPortfolioCard[],
  featuredIds: readonly string[],
  total: number
): PublicPageStory => {
  const timeline = detail?.timeline ?? [];
  const showTimeline = detail !== null && showsPublicField(detail, "timeline");
  const showPeriod = detail !== null && showsPublicField(detail, "period");
  return {
    chapter_count: showTimeline && timeline.length > 0 ? timeline.length : null,
    period: showPeriod ? storyPeriodOf(timeline) : null,
    excerpt: showTimeline ? storyExcerptOf(timeline, showPeriod) : null,
    others: storyOthersOf(others, featuredIds),
    total,
  };
};
